/**
 * "Values that did not match" (f10.4, design D10 section 3): three tabs --
 * New clients, New engagements, New elements -- each row with its decision
 * controls and source rows, and a bulk bar for engagements and elements.
 *
 * Every rule (who may be confirmed, cascades, validation) is the server's.
 * The only thing hidden client-side is what `capabilities.can_decide` says is
 * not allowed, and hidden controls are never the boundary.
 */

import { useState } from 'react';
import { Ban, Check, RotateCcw, X } from 'lucide-react';

import type {
  BulkDecisionBody,
  UsEngagementsAccount,
  UsEngagementsElement,
  UsEngagementsEngagement,
  UsEngagementsReview,
} from '@/api/client';
import {
  useDecideUsEngagementsBulk,
  useDecideUsEngagementsItem,
  type UsEngagementsDecision,
} from '@/api/queries';
import { EmptyCell } from '@/ui/EmptyCell';

import {
  AccountDecisionDialog,
  EngagementConfirmDialog,
  type AccountDecisionInput,
  type EngagementConfirmInput,
} from './DecisionDialogs';
import {
  EXCLUDED_BY_PARENT,
  decisionPill,
  formatSourceRows,
  isReversible,
  refusalMessage,
} from './usEngagementsView';

type TabId = 'accounts' | 'engagements' | 'elements';

type Dialog =
  | { type: 'account'; item: UsEngagementsAccount }
  | { type: 'engagement'; item: UsEngagementsEngagement }
  | { type: 'bulk-confirm' }
  | null;

export function UnmatchedCard({ cycleId, review }: { cycleId: string; review: UsEngagementsReview }) {
  const batchId = review.batch.id;
  const canDecide = review.capabilities.can_decide;
  const decide = useDecideUsEngagementsItem(cycleId, batchId);
  const bulk = useDecideUsEngagementsBulk(cycleId, batchId);

  const [tab, setTab] = useState<TabId>('accounts');
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [dialog, setDialog] = useState<Dialog>(null);

  const undecided =
    review.figures.accounts.undecided +
    review.figures.engagements.undecided +
    review.figures.elements.undecided;

  const switchTab = (next: TabId) => {
    setTab(next);
    setSelected(new Set());
    decide.reset();
    bulk.reset();
  };

  const closeDialog = () => {
    setDialog(null);
    decide.reset();
    bulk.reset();
  };

  const send = (decision: UsEngagementsDecision, closeOnSuccess = false) =>
    decide.mutate(decision, { onSuccess: () => closeOnSuccess && setDialog(null) });

  const sendBulk = (body: BulkDecisionBody) =>
    bulk.mutate(body, {
      onSuccess: () => {
        setSelected(new Set());
        setDialog(null);
      },
    });

  const toggle = (id: string) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const tabs: { id: TabId; label: string; count: number }[] = [
    { id: 'accounts', label: 'New clients', count: review.accounts.length },
    { id: 'engagements', label: 'New engagements', count: review.engagements.length },
    { id: 'elements', label: 'New elements', count: review.elements.length },
  ];

  const inlineError = dialog === null ? refusalMessage(decide.error ?? bulk.error) : null;
  const busy = decide.isPending || bulk.isPending;

  return (
    <div className="card">
      <div className="card-head">
        <h3>Values that did not match</h3>
        {undecided > 0 ? (
          <span className="badge pill-warning">{undecided} awaiting decision</span>
        ) : (
          <span className="badge pill-success">All decided</span>
        )}
      </div>
      <div className="card-body stack">
        <p className="hint">Nothing is added to Administration until you accept.</p>

        <div className="tabs" role="tablist" aria-label="Unmatched values">
          {tabs.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={tab === item.id}
              className={tab === item.id ? 'tab is-active' : 'tab'}
              onClick={() => switchTab(item.id)}
            >
              {item.label} <span className="count">{item.count}</span>
            </button>
          ))}
        </div>

        {inlineError && (
          <div className="alert alert-danger" role="alert">
            <div>
              <div className="a-body">{inlineError}</div>
            </div>
          </div>
        )}

        {canDecide && tab !== 'accounts' && selected.size > 0 && (
          <div className="toolbar us-eng-bulk" role="region" aria-label="Bulk decision">
            <span className="label">{selected.size} selected</span>
            <span className="spacer" />
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={busy}
              onClick={() =>
                tab === 'engagements'
                  ? setDialog({ type: 'bulk-confirm' })
                  : sendBulk({ kind: 'ELEMENT', item_ids: [...selected], decision: 'CONFIRM' })
              }
            >
              <Check aria-hidden="true" /> {tab === 'engagements' ? 'Confirm with dates…' : 'Confirm'}
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              disabled={busy}
              onClick={() =>
                sendBulk({
                  kind: tab === 'engagements' ? 'ENGAGEMENT' : 'ELEMENT',
                  item_ids: [...selected],
                  decision: 'EXCLUDE',
                })
              }
            >
              <Ban aria-hidden="true" /> Exclude
            </button>
            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setSelected(new Set())}>
              <X aria-hidden="true" /> Clear
            </button>
          </div>
        )}

        {tab === 'accounts' && (
          <AccountsTable
            items={review.accounts}
            canDecide={canDecide}
            busy={busy}
            onDecide={(item) => setDialog({ type: 'account', item })}
            onQuick={(item, decision) =>
              send({ kind: 'accounts', itemId: item.id, body: { decision, expected_version: item.version } })
            }
          />
        )}
        {tab === 'engagements' && (
          <EngagementsTable
            items={review.engagements}
            canDecide={canDecide}
            busy={busy}
            selected={selected}
            onToggle={toggle}
            onSelectAll={(ids) => setSelected(new Set(ids))}
            onConfirm={(item) => setDialog({ type: 'engagement', item })}
            onQuick={(item, decision) =>
              send({ kind: 'engagements', itemId: item.id, body: { decision, expected_version: item.version } })
            }
          />
        )}
        {tab === 'elements' && (
          <ElementsTable
            items={review.elements}
            canDecide={canDecide}
            busy={busy}
            selected={selected}
            onToggle={toggle}
            onSelectAll={(ids) => setSelected(new Set(ids))}
            onQuick={(item, decision) =>
              send({ kind: 'elements', itemId: item.id, body: { decision, expected_version: item.version } })
            }
          />
        )}
      </div>

      {dialog?.type === 'account' && (
        <AccountDecisionDialog
          account={dialog.item}
          busy={decide.isPending}
          error={decide.error}
          onClose={closeDialog}
          onSubmit={(input: AccountDecisionInput) =>
            send(
              { kind: 'accounts', itemId: dialog.item.id, body: { ...input, expected_version: dialog.item.version } },
              true,
            )
          }
        />
      )}
      {dialog?.type === 'engagement' && (
        <EngagementConfirmDialog
          engagement={dialog.item}
          busy={decide.isPending}
          error={decide.error}
          onClose={closeDialog}
          onSubmit={(input: EngagementConfirmInput) =>
            send(
              {
                kind: 'engagements',
                itemId: dialog.item.id,
                body: { decision: 'CONFIRM', ...input, expected_version: dialog.item.version },
              },
              true,
            )
          }
        />
      )}
      {dialog?.type === 'bulk-confirm' && (
        <EngagementConfirmDialog
          count={selected.size}
          busy={bulk.isPending}
          error={bulk.error}
          onClose={closeDialog}
          onSubmit={(input: EngagementConfirmInput) =>
            sendBulk({ kind: 'ENGAGEMENT', item_ids: [...selected], decision: 'CONFIRM', ...input })
          }
        />
      )}
    </div>
  );
}

type Quick = 'EXCLUDE' | 'UNDECIDED' | 'CONFIRM';

function DecisionBadge({ code, name }: { code: string; name: string }) {
  return <span className={decisionPill(code)}>{name}</span>;
}

function ParentExcluded({ parent }: { parent: 'account' | 'engagement' }) {
  return <span className="hint">Excluded with its {parent}</span>;
}

function UndoButton({ onClick, busy }: { onClick: () => void; busy: boolean }) {
  return (
    <button type="button" className="icon-btn" title="Back to awaiting decision" aria-label="Undo decision"
      disabled={busy} onClick={onClick}>
      <RotateCcw aria-hidden="true" />
    </button>
  );
}

function ExcludeButton({ onClick, busy }: { onClick: () => void; busy: boolean }) {
  return (
    <button type="button" className="btn btn-secondary btn-sm" disabled={busy} onClick={onClick}>
      <Ban aria-hidden="true" /> Exclude
    </button>
  );
}

function EmptyTab({ what }: { what: string }) {
  return <p className="hint">Every {what} in the file already exists in Administration.</p>;
}

function AccountsTable({
  items,
  canDecide,
  busy,
  onDecide,
  onQuick,
}: {
  items: readonly UsEngagementsAccount[];
  canDecide: boolean;
  busy: boolean;
  onDecide: (item: UsEngagementsAccount) => void;
  onQuick: (item: UsEngagementsAccount, decision: Quick) => void;
}) {
  if (items.length === 0) return <EmptyTab what="account" />;
  return (
    <div className="table-scroll">
      <table className="data">
        <thead>
          <tr>
            <th>Kimble account</th>
            <th>SAP ID</th>
            <th className="num">Engagements</th>
            <th className="num">Elements</th>
            <th>Proposal: Unit</th>
            <th>Source rows</th>
            <th>Decision</th>
            {canDecide && <th aria-label="Actions" />}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id}>
              <td>
                <div className="us-eng-strong">{item.raw_account_name}</div>
                {item.decision.code === 'CONFIRM' && item.decided.business_unit && (
                  <div className="hint">
                    New client &ldquo;{item.decided.name}&rdquo; under {item.decided.business_unit.name}
                  </div>
                )}
                {item.decision.code === 'MAP' && item.decided.client && (
                  <div className="hint">Mapped to {item.decided.client.name}</div>
                )}
              </td>
              <td className="mono">{item.sap_id ?? <EmptyCell />}</td>
              <td className="num">{item.engagement_count}</td>
              <td className="num">{item.element_count}</td>
              <td>
                {item.unit_candidates.map((candidate) => (
                  <div key={candidate.raw} className={candidate.business_unit ? '' : 'us-eng-unresolved'}>
                    {candidate.business_unit?.name ?? candidate.raw}{' '}
                    <span className="hint">({candidate.element_count})</span>
                  </div>
                ))}
              </td>
              <td className="hint">{formatSourceRows(item.source_row_numbers)}</td>
              <td>
                <DecisionBadge code={item.decision.code} name={item.decision.name} />
              </td>
              {canDecide && (
                <td>
                  <div className="row-actions">
                    <button type="button" className="btn btn-primary btn-sm" disabled={busy}
                      onClick={() => onDecide(item)}>
                      Decide…
                    </button>
                    {item.decision.code !== 'EXCLUDE' && (
                      <ExcludeButton busy={busy} onClick={() => onQuick(item, 'EXCLUDE')} />
                    )}
                    {isReversible(item.decision.code) && (
                      <UndoButton busy={busy} onClick={() => onQuick(item, 'UNDECIDED')} />
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SelectAll({
  ids,
  selected,
  onSelectAll,
}: {
  ids: readonly string[];
  selected: ReadonlySet<string>;
  onSelectAll: (ids: readonly string[]) => void;
}) {
  const all = ids.length > 0 && ids.every((id) => selected.has(id));
  return (
    <input
      type="checkbox"
      aria-label="Select all"
      checked={all}
      disabled={ids.length === 0}
      onChange={() => onSelectAll(all ? [] : ids)}
    />
  );
}

function EngagementsTable({
  items,
  canDecide,
  busy,
  selected,
  onToggle,
  onSelectAll,
  onConfirm,
  onQuick,
}: {
  items: readonly UsEngagementsEngagement[];
  canDecide: boolean;
  busy: boolean;
  selected: ReadonlySet<string>;
  onToggle: (id: string) => void;
  onSelectAll: (ids: readonly string[]) => void;
  onConfirm: (item: UsEngagementsEngagement) => void;
  onQuick: (item: UsEngagementsEngagement, decision: Quick) => void;
}) {
  if (items.length === 0) return <EmptyTab what="engagement" />;
  const selectable = items.filter((item) => item.decision.code !== EXCLUDED_BY_PARENT).map((item) => item.id);
  return (
    <div className="table-scroll">
      <table className="data">
        <thead>
          <tr>
            {canDecide && (
              <th>
                <SelectAll ids={selectable} selected={selected} onSelectAll={onSelectAll} />
              </th>
            )}
            <th>Code</th>
            <th>Engagement</th>
            <th>Account</th>
            <th>Stage</th>
            <th className="num">Elements</th>
            <th>Source rows</th>
            <th>Decision</th>
            {canDecide && <th aria-label="Actions" />}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const byParent = item.decision.code === EXCLUDED_BY_PARENT;
            return (
              <tr key={item.id} className={byParent ? 'us-eng-row-disabled' : undefined}>
                {canDecide && (
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Select ${item.code}`}
                      checked={selected.has(item.id)}
                      disabled={byParent}
                      onChange={() => onToggle(item.id)}
                    />
                  </td>
                )}
                <td className="mono">{item.code}</td>
                <td>
                  <div className="us-eng-strong">{item.decided.name ?? item.raw_name}</div>
                  {item.decision.code === 'CONFIRM' && item.decided.start_date && (
                    <div className="hint">
                      {item.decided.start_date} → {item.decided.end_date}
                    </div>
                  )}
                </td>
                <td>
                  {item.account_name}{' '}
                  {item.account_decision.code !== 'MATCHED' && (
                    <span className={decisionPill(item.account_decision.code)}>{item.account_decision.name}</span>
                  )}
                </td>
                <td>{item.stage.delivery_stage?.name ?? item.stage.raw ?? <EmptyCell />}</td>
                <td className="num">{item.element_count}</td>
                <td className="hint">{formatSourceRows(item.source_row_numbers)}</td>
                <td>
                  {byParent ? (
                    <ParentExcluded parent="account" />
                  ) : (
                    <DecisionBadge code={item.decision.code} name={item.decision.name} />
                  )}
                </td>
                {canDecide && (
                  <td>
                    {!byParent && (
                      <div className="row-actions">
                        <button type="button" className="btn btn-primary btn-sm" disabled={busy}
                          onClick={() => onConfirm(item)}>
                          <Check aria-hidden="true" /> Confirm…
                        </button>
                        {item.decision.code !== 'EXCLUDE' && (
                          <ExcludeButton busy={busy} onClick={() => onQuick(item, 'EXCLUDE')} />
                        )}
                        {isReversible(item.decision.code) && (
                          <UndoButton busy={busy} onClick={() => onQuick(item, 'UNDECIDED')} />
                        )}
                      </div>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ElementsTable({
  items,
  canDecide,
  busy,
  selected,
  onToggle,
  onSelectAll,
  onQuick,
}: {
  items: readonly UsEngagementsElement[];
  canDecide: boolean;
  busy: boolean;
  selected: ReadonlySet<string>;
  onToggle: (id: string) => void;
  onSelectAll: (ids: readonly string[]) => void;
  onQuick: (item: UsEngagementsElement, decision: Quick) => void;
}) {
  if (items.length === 0) return <EmptyTab what="element" />;
  const selectable = items.filter((item) => item.decision.code !== EXCLUDED_BY_PARENT).map((item) => item.id);
  return (
    <div className="table-scroll">
      <table className="data">
        <thead>
          <tr>
            {canDecide && (
              <th>
                <SelectAll ids={selectable} selected={selected} onSelectAll={onSelectAll} />
              </th>
            )}
            <th>Reference</th>
            <th>Engagement</th>
            <th>Product</th>
            <th>Proposal: Unit</th>
            <th>Source rows</th>
            <th>Decision</th>
            {canDecide && <th aria-label="Actions" />}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => {
            const byParent = item.decision.code === EXCLUDED_BY_PARENT;
            return (
              <tr key={item.id} className={byParent ? 'us-eng-row-disabled' : undefined}>
                {canDecide && (
                  <td>
                    <input
                      type="checkbox"
                      aria-label={`Select ${item.code}`}
                      checked={selected.has(item.id)}
                      disabled={byParent}
                      onChange={() => onToggle(item.id)}
                    />
                  </td>
                )}
                <td className="mono">{item.code}</td>
                <td>
                  <span className="mono">{item.engagement_code}</span>{' '}
                  {item.engagement_decision.code !== 'MATCHED' && (
                    <span className={decisionPill(item.engagement_decision.code)}>
                      {item.engagement_decision.name}
                    </span>
                  )}
                </td>
                <td>{item.product ?? <EmptyCell />}</td>
                <td className={item.unit.business_unit ? undefined : 'us-eng-unresolved'}>
                  {item.unit.business_unit?.name ?? item.unit.raw}
                </td>
                <td className="hint">{formatSourceRows(item.source_row_numbers)}</td>
                <td>
                  {byParent ? (
                    <ParentExcluded parent="engagement" />
                  ) : (
                    <DecisionBadge code={item.decision.code} name={item.decision.name} />
                  )}
                </td>
                {canDecide && (
                  <td>
                    {!byParent && (
                      <div className="row-actions">
                        {item.decision.code !== 'CONFIRM' && (
                          <button type="button" className="btn btn-primary btn-sm" disabled={busy}
                            onClick={() => onQuick(item, 'CONFIRM')}>
                            <Check aria-hidden="true" /> Confirm
                          </button>
                        )}
                        {item.decision.code !== 'EXCLUDE' && (
                          <ExcludeButton busy={busy} onClick={() => onQuick(item, 'EXCLUDE')} />
                        )}
                        {isReversible(item.decision.code) && (
                          <UndoButton busy={busy} onClick={() => onQuick(item, 'UNDECIDED')} />
                        )}
                      </div>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
